import { z } from 'zod';
import {
  ParticipantStatus,
  PushKind,
  PushStatus,
  RoundStatus,
  SessionKind,
  SignOffStatus,
  TournamentKind,
  TournamentStatus,
} from '../enums';
import { LocaleSchema } from '../i18n';

/**
 * Native app (apps/mobile) <-> api (modules/mobile, modules/notifications) contract.
 * CLAUDE.md §5 "mobile", §16. FROZEN (lead scaffold): the api-mobile package and the mobile app
 * packages code against this file in parallel. Changes must be additive (old app versions stay in
 * the stores during review, §15 #343).
 */

// ---------------------------------------------------------------------------------------------
// shared constants
// ---------------------------------------------------------------------------------------------

/** request header every app request carries: `mobile/<version> (<android|ios>; <build>; <variant>)` */
export const MOBILE_CLIENT_HEADER = 'X-PinGo-Client';

/** app variants (APP_VARIANT) — §16.10 */
export const AppVariant = z.enum(['development', 'preview', 'production']);
export type AppVariant = z.infer<typeof AppVariant>;

/** custom URL scheme per variant (several variants may be installed on one device) */
export const APP_SCHEMES: Record<AppVariant, string> = {
  development: 'pingo-dev',
  preview: 'pingo-preview',
  production: 'pingo',
};

/**
 * Link paths the app claims (App Links / Universal Links / custom scheme). Everything else — the
 * admin console /a/*, the TV board /t/<token>/tv, any other page — stays on the web (§16.8).
 */
export const APP_LINK_PATH_PREFIXES = ['/t/', '/c/', '/p/', '/r/', '/join/'] as const;

/** apple-app-site-association `components` (order matters: exclusions first) — §16.8 */
export const AASA_COMPONENTS: ReadonlyArray<{ '/': string; exclude?: true; comment?: string }> = [
  { '/': '/a/*', exclude: true, comment: 'admin console stays on the web' },
  { '/': '/t/*/tv', exclude: true, comment: 'TV board stays on the web' },
  { '/': '/t/*/ceremony', exclude: true, comment: 'ceremony audience screen stays on the web' },
  { '/': '/t/*' },
  { '/': '/c/*' },
  { '/': '/p/*' },
  { '/': '/r/*' },
  { '/': '/join/*' },
];

const SEMVER = /^\d+\.\d+\.\d+$/;
export const SemverSchema = z.string().regex(SEMVER, 'expected x.y.z');

/** lower-case platform used on the wire (query strings, device registration) */
export const MobilePlatformParam = z.enum(['android', 'ios']);
export type MobilePlatformParam = z.infer<typeof MobilePlatformParam>;

/**
 * Compare two x.y.z versions (-1 | 0 | 1). Pure, shared by the api version gate and the app.
 * Pre-release / build suffixes are not part of the contract (SemverSchema rejects them).
 */
export function compareSemver(a: string, b: string): -1 | 0 | 1 {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < 3; i++) {
    const x = pa[i] ?? 0;
    const y = pb[i] ?? 0;
    if (x !== y) return x < y ? -1 : 1;
  }
  return 0;
}

// ---------------------------------------------------------------------------------------------
// GET /mobile/version
// ---------------------------------------------------------------------------------------------

export const MobileVersionQuerySchema = z.object({
  platform: MobilePlatformParam,
  version: SemverSchema,
  build: z.string().max(32).optional(),
});
export type MobileVersionQuery = z.infer<typeof MobileVersionQuerySchema>;

export const MobileVersionResponseSchema = z.object({
  platform: MobilePlatformParam,
  /** the version the app sent */
  current: SemverSchema,
  minSupported: SemverSchema,
  latest: SemverSchema,
  /** current < minSupported → app shows the blocking update screen (caddy queue keeps sending) */
  updateRequired: z.boolean(),
  /** current < latest → app shows a one-time banner */
  updateAvailable: z.boolean(),
  storeUrl: z.string().nullable(),
  /** web origin of the share links (admin console "open in browser", "open on the web") */
  webBaseUrl: z.string(),
});
export type MobileVersionResponse = z.infer<typeof MobileVersionResponseSchema>;

// ---------------------------------------------------------------------------------------------
// well-known verification files (GET /mobile/well-known/*) — shapes for tests / tooling
// ---------------------------------------------------------------------------------------------

export const AssetLinksSchema = z.array(
  z.object({
    relation: z.array(z.literal('delegate_permission/common.handle_all_urls')),
    target: z.object({
      namespace: z.literal('android_app'),
      package_name: z.string(),
      sha256_cert_fingerprints: z.array(z.string().regex(/^([0-9A-F]{2}:){31}[0-9A-F]{2}$/)),
    }),
  }),
);
export type AssetLinks = z.infer<typeof AssetLinksSchema>;

export const AppleAppSiteAssociationSchema = z.object({
  applinks: z.object({
    details: z.array(
      z.object({
        appIDs: z.array(z.string()),
        components: z.array(
          z.object({
            '/': z.string(),
            exclude: z.boolean().optional(),
            comment: z.string().optional(),
          }),
        ),
      }),
    ),
  }),
});
export type AppleAppSiteAssociation = z.infer<typeof AppleAppSiteAssociationSchema>;

// ---------------------------------------------------------------------------------------------
// push: preferences + payload
// ---------------------------------------------------------------------------------------------

/** DeviceToken.preferences — every kind on by default; off = never enqueued for that device */
export const PushPreferencesSchema = z.object({
  roundStarted: z.boolean().default(true),
  signoffRequested: z.boolean().default(true),
  resultsFinalized: z.boolean().default(true),
  teeTimeReminder: z.boolean().default(true),
});
export type PushPreferences = z.infer<typeof PushPreferencesSchema>;
export const DEFAULT_PUSH_PREFERENCES: PushPreferences = {
  roundStarted: true,
  signoffRequested: true,
  resultsFinalized: true,
  teeTimeReminder: true,
};
/** PushKind → preference key */
export const PUSH_PREFERENCE_KEY: Record<PushKind, keyof PushPreferences> = {
  ROUND_STARTED: 'roundStarted',
  SIGNOFF_REQUESTED: 'signoffRequested',
  RESULTS_FINALIZED: 'resultsFinalized',
  TEE_TIME_REMINDER: 'teeTimeReminder',
};

/** Android notification channels created by the app (§16.7) */
export const PushChannel = z.enum(['scoring', 'results']);
export type PushChannel = z.infer<typeof PushChannel>;
export const PUSH_CHANNEL_OF: Record<PushKind, PushChannel> = {
  ROUND_STARTED: 'scoring',
  TEE_TIME_REMINDER: 'scoring',
  SIGNOFF_REQUESTED: 'results',
  RESULTS_FINALIZED: 'results',
};

/**
 * Notification `data` (PushMessage.data). NO secrets: never a PIN, a bearer token, a caddy link
 * token or a player access token — only the public leaderboard token may appear inside `path`
 * (§15 #349). `path` is an app path (/caddy, /player, /account/entries/<id>, /t/<publicToken>,
 * /account); the app still routes it through its path whitelist.
 */
export const PushDataSchema = z.object({
  kind: PushKind,
  path: z
    .string()
    .max(300)
    .regex(/^\/[A-Za-z0-9/_-]*$/, 'app path'),
  tournamentId: z.uuid(),
  roundId: z.uuid().optional(),
});
export type PushData = z.infer<typeof PushDataSchema>;

// ---------------------------------------------------------------------------------------------
// device registration: PUT / PATCH / DELETE /mobile/devices/:installationId
// ---------------------------------------------------------------------------------------------

export const InstallationIdParamSchema = z.object({ installationId: z.uuid() });

/** Expo push token as returned by getExpoPushTokenAsync */
export const ExpoPushTokenSchema = z
  .string()
  .max(200)
  .regex(/^(ExponentPushToken|ExpoPushToken)\[[^\]]+\]$/, 'Expo push token');

/** raw native FCM registration token (getDevicePushTokenAsync on Android, §15 #458) */
export const FcmPushTokenSchema = z
  .string()
  .min(1)
  .max(4096)
  .regex(/^\S+$/, 'FCM registration token');

/** how the server delivers a device row: Expo push service or FCM HTTP v1 directly */
export const PushTokenType = z.enum(['expo', 'fcm']);
export type PushTokenType = z.infer<typeof PushTokenType>;

export const DeviceRegisterRequestSchema = z
  .object({
    /** the push token: an Expo token (tokenType expo) or a raw FCM token (tokenType fcm) */
    expoToken: z.string().min(1).max(4096),
    /** omitted = 'expo' (apps before §15 #458) */
    tokenType: PushTokenType.optional(),
    platform: MobilePlatformParam,
    /** app semver (package.json version), e.g. 1.2.0 */
    appVersion: SemverSchema,
    buildNumber: z.string().max(32).optional(),
    locale: LocaleSchema,
    /** omitted = keep the stored preferences (defaults for a new row) */
    preferences: PushPreferencesSchema.partial().optional(),
  })
  .superRefine((v, ctx) => {
    const schema = v.tokenType === 'fcm' ? FcmPushTokenSchema : ExpoPushTokenSchema;
    const r = schema.safeParse(v.expoToken);
    if (!r.success) {
      ctx.addIssue({
        code: 'custom',
        path: ['expoToken'],
        message: v.tokenType === 'fcm' ? 'FCM registration token' : 'Expo push token',
      });
    }
    // an Expo-shaped string is always routed to Expo: never register it as an FCM token
    if (v.tokenType === 'fcm' && /^Expo(nent)?PushToken\[/.test(v.expoToken)) {
      ctx.addIssue({ code: 'custom', path: ['tokenType'], message: 'Expo token sent as fcm' });
    }
  });
export type DeviceRegisterRequest = z.infer<typeof DeviceRegisterRequestSchema>;

export const DeviceUpdateRequestSchema = z
  .object({
    preferences: PushPreferencesSchema.partial().optional(),
    locale: LocaleSchema.optional(),
  })
  .refine((v) => v.preferences !== undefined || v.locale !== undefined, {
    message: 'nothing to update',
  });
export type DeviceUpdateRequest = z.infer<typeof DeviceUpdateRequestSchema>;

/** the calling session's own row (never another principal's) */
export const DeviceRegistrationSchema = z.object({
  installationId: z.uuid(),
  sessionKind: SessionKind,
  platform: MobilePlatformParam,
  appVersion: z.string(),
  locale: LocaleSchema,
  preferences: PushPreferencesSchema,
  /** set when Expo answered DeviceNotRegistered; cleared by the next PUT */
  disabled: z.boolean(),
  updatedAt: z.iso.datetime(),
});
export type DeviceRegistration = z.infer<typeof DeviceRegistrationSchema>;

// ---------------------------------------------------------------------------------------------
// GET /mobile/me/entries (USER) — verified profile links only (§7.13)
// ---------------------------------------------------------------------------------------------

export const MobileEntryRoundSchema = z.object({
  roundId: z.uuid(),
  roundNo: z.number().int(),
  /** yyyy-MM-dd (tournament timezone) */
  date: z.string().nullable(),
  status: RoundStatus,
  groupName: z.string().nullable(),
  startHole: z.number().int().nullable(),
  /** absolute instant (render in tournament.timezone) */
  teeTime: z.iso.datetime().nullable(),
  /** current sign-off of this participant for the round */
  signOffStatus: SignOffStatus.nullable(),
});
export type MobileEntryRound = z.infer<typeof MobileEntryRoundSchema>;

export const MobileEntrySchema = z.object({
  tournament: z.object({
    id: z.uuid(),
    name: z.string(),
    kind: TournamentKind,
    status: TournamentStatus,
    timezone: z.string(),
    startDate: z.string(),
    endDate: z.string(),
    clubId: z.uuid().nullable(),
    allowSelfEntry: z.boolean(),
    /** only when the public leaderboard is enabled */
    publicToken: z.string().nullable(),
  }),
  participant: z.object({
    id: z.uuid(),
    status: ParticipantStatus,
    bibNumber: z.number().int().nullable(),
    fullName: z.string(),
  }),
  rounds: z.array(MobileEntryRoundSchema),
});
export type MobileEntry = z.infer<typeof MobileEntrySchema>;

export const MobileEntriesResponseSchema = z.object({ items: z.array(MobileEntrySchema) });
export type MobileEntriesResponse = z.infer<typeof MobileEntriesResponseSchema>;

// ---------------------------------------------------------------------------------------------
// POST /mobile/player-session (USER) → SessionResponse (kind PLAYER, scope PLAYER_VIEW)
// ---------------------------------------------------------------------------------------------

export const MobilePlayerSessionRequestSchema = z.object({
  participantId: z.uuid(),
  /** installation id (audit / Session.deviceId) */
  deviceId: z.string().max(64).optional(),
});
export type MobilePlayerSessionRequest = z.infer<typeof MobilePlayerSessionRequestSchema>;

// ---------------------------------------------------------------------------------------------
// GET /mobile/push/stats (SA)
// ---------------------------------------------------------------------------------------------

export const MobilePushStatsSchema = z.object({
  since: z.iso.datetime(),
  /** PushMessage counts of the last 24 h by status (every status present, 0 when none) */
  messages: z.record(PushStatus, z.number().int()),
  /** active (enabled, live session) device rows */
  devices: z.array(
    z.object({ platform: MobilePlatformParam, sessionKind: SessionKind, count: z.number().int() }),
  ),
  topErrors: z.array(z.object({ error: z.string(), count: z.number().int() })).max(10),
});
export type MobilePushStats = z.infer<typeof MobilePushStatsSchema>;
